import { Suspense, useCallback, useEffect, useRef, type ComponentType } from 'react';
import { useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { useAppStore } from '@/app/appStore';
import { useResolvedTheme } from '@/app/theme';
import { useAuth } from '@/auth/AuthProvider';
import { useChatStore } from '@/chat/chatStore';
import { FallbackSurface } from '@/screens/FallbackSurface';
import { resolveSurface } from '@/screens/registry';
import { type PanelProps } from '@/screens/types';
import { PanelActionTooltip } from '@/shared/PanelActionTooltip';
import { createBus } from './detachBus';
import {
  clearDetachedBootstrap,
  readDetachedBootstrap,
  resolveDetachSessionId,
} from './detachedState';
import { usePanelStore } from './panelStore';

/**
 * Renders a single panel in a detached browser window.
 */
export function DetachedPanel() {
  const { t } = useTranslation('errors');
  const { t: tc } = useTranslation('common');
  const { user } = useAuth();
  const sessionId = useChatStore((s) => s.sessionId);
  const params = useParams({ from: '/workbench/panel/$panelId' });
  const panel = usePanelStore((s) => s.panels.find((p) => p.panelId === params.panelId));
  const patchPanelPayload = usePanelStore((s) => s.patchPanelPayload);
  const replacePanel = usePanelStore((s) => s.replacePanel);
  const closePanel = usePanelStore((s) => s.closePanel);
  const themePreference = useAppStore((s) => s.theme);
  const resolvedTheme = useResolvedTheme(themePreference);
  const lastPanelRef = useRef(panel);
  const entry = panel ? resolveSurface(panel.surface) : null;
  const { t: tSurface } = useTranslation(entry?.i18nNamespace ?? '');

  const applyTheme = useCallback((theme: string) => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.classList.toggle('light', theme === 'light');
  }, []);

  useEffect(() => {
    lastPanelRef.current = panel;
  }, [panel]);

  useEffect(() => {
    const bootstrap = readDetachedBootstrap(params.panelId);
    if (bootstrap) {
      replacePanel(bootstrap.panel);
    }

    const bootstrapTheme = bootstrap?.theme;
    const effectiveTheme =
      bootstrapTheme === 'dark' || bootstrapTheme === 'light' ? bootstrapTheme : resolvedTheme;

    applyTheme(effectiveTheme);
  }, [applyTheme, params.panelId, replacePanel, resolvedTheme]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;

    const bus = createBus(resolveDetachSessionId(sessionId, user?.sub));
    const unsubscribe = bus.subscribe((msg) => {
      if (msg.panelId !== params.panelId) return;

      if (msg.kind === 'panel_opened' || msg.kind === 'panel_payload_patch') {
        const data = msg.data as { panel?: unknown } | undefined;
        const nextPanel = data?.panel;
        if (nextPanel && typeof nextPanel === 'object' && 'panelId' in nextPanel) {
          replacePanel(nextPanel as NonNullable<typeof panel>);
        }
        return;
      }

      if (msg.kind === 'panel_closed') {
        clearDetachedBootstrap(params.panelId);
        closePanel(params.panelId);
        window.close();
        return;
      }

      if (msg.kind === 'theme_changed') {
        const data = msg.data as { theme?: string } | undefined;
        if (data?.theme) {
          applyTheme(data.theme);
        }
      }
    });

    const publishSnapshot = () => {
      const current = lastPanelRef.current;
      if (!current) return;
      bus.publish({
        kind: 'panel_opened',
        panelId: current.panelId,
        data: { panel: current },
      });
    };

    const handleBeforeUnload = () => {
      const current = lastPanelRef.current;
      if (!current) return;
      clearDetachedBootstrap(current.panelId);
      bus.publish({
        kind: 'panel_closed',
        panelId: current.panelId,
        data: { updatedAt: Date.now() },
      });
    };

    window.addEventListener('focus', publishSnapshot);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('focus', publishSnapshot);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      unsubscribe();
      bus.close();
    };
  }, [applyTheme, closePanel, params.panelId, replacePanel, sessionId, user?.sub]);

  const onPatchPayload = useCallback(
    (next: unknown) => {
      if (!panel) return;
      const updatedAt = Date.now();
      patchPanelPayload(panel.panelId, next, updatedAt);

      if (typeof BroadcastChannel === 'undefined') return;
      const bus = createBus(resolveDetachSessionId(sessionId, user?.sub));
      const nextPanel = {
        ...panel,
        payload:
          typeof panel.payload === 'object' &&
          panel.payload !== null &&
          typeof next === 'object' &&
          next !== null
            ? { ...panel.payload, ...(next as object) }
            : next,
        updatedAt,
      };
      bus.publish({
        kind: 'panel_payload_patch',
        panelId: panel.panelId,
        data: { panel: nextPanel },
      });
      bus.close();
    },
    [panel, patchPanelPayload, sessionId, user?.sub],
  );

  const onClose = useCallback(() => {
    if (!panel) return;
    clearDetachedBootstrap(panel.panelId);

    if (typeof BroadcastChannel !== 'undefined') {
      const bus = createBus(resolveDetachSessionId(sessionId, user?.sub));
      bus.publish({
        kind: 'panel_closed',
        panelId: panel.panelId,
        data: { updatedAt: Date.now() },
      });
      bus.close();
    }

    closePanel(panel.panelId);
    window.close();
  }, [closePanel, panel, sessionId, user?.sub]);

  if (!panel) {
    return (
      <div className="text-muted-foreground flex h-screen items-center justify-center p-6 text-sm">
        {t('notFound')}
      </div>
    );
  }

  const SurfaceComponent: ComponentType<PanelProps> = entry?.component ?? FallbackSurface;
  const payload = entry == null ? { surfaceKey: panel.surface } : panel.payload;

  const title = panel.title ?? entry?.i18nNamespace.replace(/^surface\./, '') ?? panel.surface;

  const description = panel.description ?? tSurface('description', { defaultValue: '' });
  const descId = `panel-desc-${panel.panelId}`;

  return (
    <div
      className="bg-background flex h-screen flex-col overflow-hidden"
      aria-describedby={description ? descId : undefined}
    >
      {/* Chrome header — mirrors PanelShell so detached popouts are visually consistent */}
      {description ? (
        <p id={descId} className="sr-only">
          {description}
        </p>
      ) : null}
      <div className="border-border bg-card flex items-center justify-between border-b px-3 py-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="text-foreground truncate text-sm font-medium">{title}</span>
          {panel.badge ?? null}
          {description ? (
            <PanelActionTooltip label={description}>
              <span
                role="img"
                aria-label={tc('panelActions.info', { defaultValue: 'Panel information' })}
                className="text-muted-foreground flex-shrink-0"
                data-testid="panel-info-icon"
              >
                <Info size={12} aria-hidden="true" />
              </span>
            </PanelActionTooltip>
          ) : null}
        </div>
        <button
          type="button"
          aria-label={tc('panelActions.close', { defaultValue: 'Close panel' })}
          onClick={onClose}
          className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
        >
          <span className="sr-only">{tc('close')}</span>×
        </button>
      </div>
      {panel.contextSlot ? (
        <div className="border-border bg-card text-muted-foreground border-b px-3 py-1 text-xs">
          {panel.contextSlot}
        </div>
      ) : null}
      <div className="flex-1 overflow-auto p-3">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center">
              <div className="border-primary h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
            </div>
          }
        >
          <SurfaceComponent
            panelId={panel.panelId}
            payload={payload}
            onClose={onClose}
            onPatchPayload={onPatchPayload}
            onSaveTemplate={() => {
              // Detached popup does not own template persistence.
            }}
          />
        </Suspense>
      </div>
    </div>
  );
}
